import ApiConfig from '@api';
import Channel from '../classes/channel';
import Content from '../types/enums/content';
import DeviceConfig from '@devices/config';
import Event from '../classes/event';
import FastEvent from '../classes/fastEvent';
import FlavourConfig from '../config/flavours';
import Genre from '../classes/genre';
import IStream from '@shared/types/interfaces/stream';
import MediaProduction from '../classes/mediaProduction';
import Package from '../../package.json';
import RegularEvent from '../classes/regularEvent';
import User from '../classes/user';
import TranslationManager from '../assets/locale/translationsManager';
import HTTP_ERRORS from '../types/enums/httpErrors';
import PLAYER_LICENSE_ERRORS from '../types/enums/playerErrors';
// import { setState } from '../store/slices/media.slice';
import { store } from '../store/store';
import { IPlayerError } from '@shared/types/interfaces/error';
import {
  PlayerError,
  PlayerState,
  GeoBlockErrors,
  GeoBlockErrorsMessage,
  PlayerType,
} from '../types/enums/player';
import ApiUtils from './api';

enum PLAYBACK_TYPE {
  AVOD = 'AVOD',
  CATCH_UP = 'Catch up',
  LIVE = 'Live',
  NPVR = 'nPVR',
  START_OVER = 'Start over',
  SVOD = 'SVOD',
}

interface MEDIA_CONTENT {
  secure: string;
  uri: string;
  drm_scheme?: string;
  drm_license_uri?: string;
  drm_license_header?: [string, string | null | undefined][];
}

const PlayerUtils = {
  checkPlayback(state: PlayerState) {
    let playbackAvailable = false;
    // Playing is available when not on these states
    if (
      ![
        PlayerState.ERROR,
        PlayerState.PAUSE,
        PlayerState.LOADED,
        PlayerState.SEEK,
        PlayerState.SEEKING,
        // PlayerState.WAITING,
      ].includes(state)
    ) {
      setTimeout(() => {
        // store.dispatch(setState(PlayerState.PLAYING));
      });
      playbackAvailable = true;
    }
    return playbackAvailable;
  },
  getMediaContent(stream: IStream) {
    if (stream) {
      const content: MEDIA_CONTENT = {
        secure: 'false', // If true this uses HW_SECURE
        uri: stream.uri,
      };
      const drm = stream.drm;
      if (drm) {
        const headers = PlayerUtils.getContentHeaders();
        content.drm_scheme = drm.key;
        content.drm_license_uri = drm.license;
        content.drm_license_header = [
          ['Origin', ApiUtils.getUrl('origin')],
          ['Referer', ApiUtils.getUrl('referer')],
          ['Content-Type', headers.contentType],
          ['X-Access-Token', headers.accessToken],
          ['X-Tvup-Device', headers.deviceId],
          ['X-Tvup-Planid', headers.planId],
          ['X-Tvup-Platform', 'SMARTTV'], // TBD: Change the device to KEPLER if possible
          ['X-Tvup-Userid', headers.userId], // TBD: Createidrequest doesn't work right now for anonymous drm playback
        ];
      }
      return content;
    }
    return {};
  },
  getChannelDrm(channel?: Channel) {
    const { user } = store.getState();
    return {
      key: 'com.widevine.alpha',
      license:
        (!user.isAnonymous
          ? ApiConfig.urls.app.channelLicense
          : ApiConfig.urls.app.freeChannelLicense) + channel?.code,
    };
  },
  getVodDrm(asset?: FastEvent) {
    const { user } = store.getState();
    return {
      key: 'com.widevine.alpha',
      license:
        (!user.isAnonymous
          ? ApiConfig.urls.app.vodLicense
          : ApiConfig.urls.app.freeVodLicense) + asset?.eventId,
    };
  },
  getContentHeaders() {
    const { user } = store.getState();
    return {
      accessToken: user.token,
      contentType: 'application/octet-stream',
      deviceId: DeviceConfig.info.uuid,
      planId: User.getPlan(user?.planId || '')?.id,
      platform: DeviceConfig.info.platform,
      userId: user.isAnonymous ? user.userId : '',
    };
  },
  getContentPackage() {
    const { user } = store.getState();
    let pkg = '';
    if (user?.extraPlans?.length) {
      user.extraPlans
        .map((plan) => plan.code)
        .sort()
        .forEach((item) => {
          pkg += '+' + item;
        });
      pkg = pkg.substring(1, pkg.length);
    }
    return pkg;
  },
  getProgramTitle(event?: Event | null) {
    // Retrieve program title data
    const episode = event?.episodeNumber ?? '';
    const season = event?.seasonNumber ?? '';
    const title = event?.episodeTitle || event?.title;

    // Build program title
    let program = '';
    if (title) program += title;
    if (program && season && episode)
      program = `${program} - ${season} - ${episode}`;
    return program;
  },
  getFullProgramTitle(event?: Event | null) {
    // Retrieve program title data
    const episode = event?.episodeNumber ?? '';
    const season = event?.seasonNumber ?? '';
    let program = event?.title;
    if(event?.episodeTitle) {
      program  = `${program} - ${event?.episodeTitle}`;
    }

    // Build program title
    if (program && season && episode)
      program = `${program} - ${season} - ${episode}`;
    return program;
  },
  getPlaybackType(event?: Event | null) {
    switch (event?.type) {
      case Content.EPG_EVENT:
        const { media, router } = store.getState();
        const startover =
          (media.startover || router.params.current?.restart) ?? false;
        if (startover) return PLAYBACK_TYPE.START_OVER; // Start Over: kind=”EpgEvent” && restart in params
        return PLAYBACK_TYPE.LIVE; // Live: kind=”EpgEvent”
      case Content.AVOD:
      case Content.FAST_EVENT:
      case Content.SHOW:
      case Content.SVOD:
        if (event?.channel?.type === Content.SVOD) {
          return PLAYBACK_TYPE.SVOD; // AVOD: kind="FastEvent" && type="SVOD"
        }
        return PLAYBACK_TYPE.AVOD; // AVOD: kind="FastEvent" && type="AVOD"
      case Content.MEDIA_PRODUCTION:
      case Content.RECORDING_EVENT:
        const mediaProductionEvent = event as MediaProduction;
        const { recordings } = store.getState();
        const identifier = recordings.identifiers.find(
          (id) =>
            id.code === mediaProductionEvent.eventId ||
            id.code === mediaProductionEvent.seasonId
        );
        if (identifier) return PLAYBACK_TYPE.NPVR; // nPVR: kind=”MediaProduction” && in recordings
        return PLAYBACK_TYPE.CATCH_UP; // Catch up: kind=”MediaProduction” && catchup=true
    }
    return '';
  },
  getGenericMetadata(channel?: Channel, event?: Event | null) {
    const { media, user } = store.getState();
    return {
      enableAnalytics: true,
      'app.name': FlavourConfig.appTitle,
      'app.releaseVersion': Package.version,
      'content.bitrate': media.bitrate,
      'content.channel': channel?.name || '',
      'content.customDimension.1': User.getPlan(user.planId)?.name || '',
      'content.customDimension.2': user?.operator?.name,
      'content.episodeTitle': event?.episodeTitle || '',
      'content.genre':
        Genre.getGenreById((event?.genreId as string) || '')?.name || '',
      'content.id': event?.eventId || '',
      'device.id': DeviceConfig.info.uuid || null,
      'content.isLive': event?.type === Content.EPG_EVENT,
      'content.language': event?.language || '',
      'content.package': this.getContentPackage() || '',
      'content.playbackType': this.getPlaybackType(event),
      'content.program': this.getProgramTitle(event) || '',
      'content.resolution': channel?.resolution,
      'content.season': event?.seasonNumber,
      'content.title': channel?.title || '',
      'content.tvShow': event?.title,
      'user.name': user?.userId,
      'user.type': user?.isAnonymous ? 'Unregistered' : 'Registered',
      'parse.CDNNode': true,
    };
  },
  getZapperMetadata(channel?: Channel) {
    const { currentEvent: event } = channel || {};
    return this.getGenericMetadata(channel, event);
  },
  getPlayerMetadata(asset?: RegularEvent) {
    return this.getGenericMetadata(asset?.channel, asset);
  },
  getDashError(
    code?: string,
    httpCode?: HTTP_ERRORS,
    serverError?: PLAYER_LICENSE_ERRORS | null
  ) {
    const { media } = store.getState();
    // (10-35) https://cdn.dashjs.org/latest/jsdoc/core_erreturn message:ors_Errors.js.html
    // (100-114) https://cdn.dashjs.org/latest/jsdoc/streaming_protection_errors_ProtectionErrors.js.html#sunlight-1-line-96
    // (200-201) https://cdn.dashjs.org/latest/jsdoc/mss_errors_MssErrors.js.html
    // (11000-11013) https://cdn.dashjs.org/latest/jsdoc/offline_errors_OfflineErrors.js.html
    let type: PlayerError | null = PlayerError.UNKNOWN;
    switch (code) {
      case '10':
      case '11':
      case '12':
      case '15':
        type = PlayerError.LOAD;
        break;
      case '16':
        // type = PlayerError.SYNCHRONIZATION;
        type = null;
        break;
      case '17':
      case '18':
        // type = PlayerError.FRAGMENT;
        type = null;
        break;
      case '19':
        type = PlayerError.URL;
        break;
      case '20':
      case '21':
        // type = PlayerError.SOURCEBUFFER;
        type = null;
        break;
      case '22':
        // type = PlayerError.UPDATE;
        type = null;
        break;
      case '25':
        type = PlayerError.MEDIASOURCE;
        break;
      case '23':
      case '35':
        type = null;
        break;
      case '24':
      case '100':
      case '101':
      case '102':
      case '103':
      case '104':
      case '105':
      case '106':
      case '107':
      case '108':
      case '109':
      case '110':
      case '111':
      case '112':
      case '113':
      case '114':
        type = this.getErrorType(
          PlayerError.PROTECTION,
          httpCode,
          serverError
        ) as PlayerError;
        break;
      case '25':
      case '26':
      case '27':
      case '28':
      case '29':
        type = PlayerError.DOWNLOAD;
        break;
      case '31':
      case '32':
      case '34':
        type = media?.live ? null : PlayerError.MANIFEST;
        break;
      case '33':
        // type = PlayerError.SUBTITLES;
        type = null;
        break;
      case '200':
      case '201':
        // type = PlayerError.MSS;
        type = null;
        break;
      case '11000':
      case '11001':
      case '11002':
      case '11003':
      case '11004':
      case '11005':
      case '11006':
      case '11007':
      case '11008':
      case '11009':
      case '11010':
      case '11011':
      case '11012':
      case '11013':
        type = PlayerError.OFFLINE;
        break;
    }
    return type;
  },
  getHlsjsError(error?: string, request?: XMLHttpRequest) {
    if (request) {
      const geoBlockError = this.getGeoBlockError(
        request,
        true,
        null,
        request.status
      );
      if (geoBlockError) {
        return geoBlockError;
      }
    }
    let parsedError = error?.replace(/([a-z])([A-Z])/g, '$1 $2') || '';
    parsedError = parsedError?.charAt(0).toUpperCase() + parsedError?.slice(1);
    return parsedError;
  },
  getAvPlayerError(error?: string, errorStatus?: number) {
    if (errorStatus) {
      const geoBlockError = this.getGeoBlockError(
        null,
        false,
        null,
        errorStatus
      );
      if (geoBlockError) {
        return geoBlockError;
      }
    }
    return error;
  },
  parseShakaError(event: {
    detail: {
      code: number;
      data: {
        data: (number | string)[];
      }[];
    };
  }) {
    let type = null;
    const code = event?.detail?.code?.toString() || 'Unknown';
    let serverError = null;
    let httpError = null;
    if (event?.detail?.data?.length) {
      if (event?.detail?.data[0]?.data?.length > 2) {
        httpError = event?.detail?.data[0].data[1];
        const message = event?.detail?.data[0].data[2];
        serverError = PlayerUtils.getServerError(message as string);
        if (event?.detail?.data?.length > 3) {
          const reasonHeaders = event?.detail?.data[3] as {
            [key: string]: any;
          };
          const reason = reasonHeaders['x-tvup-reason'];
          const geoBlockError = PlayerUtils.getGeoBlockError(
            null,
            false,
            reason,
            httpError as HTTP_ERRORS
          );
          if (geoBlockError) {
            type = geoBlockError;
          }
        }
      }
      if (!type) {
        type = PlayerUtils.getShakaError(
          code,
          httpError as HTTP_ERRORS,
          serverError as PLAYER_LICENSE_ERRORS
        );
      }
      if (type) {
        const error: IPlayerError = {
          code,
          player: PlayerType.SHAKA,
          type,
        };
        return error;
      }
    }
  },
  getShakaError(
    code?: string,
    httpCode?: HTTP_ERRORS,
    serverError?: PLAYER_LICENSE_ERRORS | null
  ) {
    const { media } = store.getState();
    // https://shaka-player-demo.appspot.com/docs/api/shaka.util.Error.html
    let type: PlayerError | null = PlayerError.UNKNOWN;
    switch (code?.slice(0, -3)) {
      case '1':
        type = PlayerError.NETWORK;
        break;
      case '2':
        // type = PlayerError.TEXT;
        type = null;
        break;
      case '3':
        type = PlayerError.MEDIA;
        break;
      case '4':
        type = media?.live ? null : PlayerError.MANIFEST;
        break;
      case '5':
        type = PlayerError.STREAMING;
        break;
      case '6':
        type = this.getErrorType(
          PlayerError.DRM,
          httpCode,
          serverError
        ) as PlayerError;
        break;
      case '7':
        type = PlayerError.PLAYER;
        break;
      case '8':
        // type = PlayerError.CAST;
        type = null;
        break;
      case '9':
        // type = PlayerError.STORAGE;
        type = null;
        break;
      case '10':
        // type = PlayerError.ADS;
        type = null;
        break;
    }
    return type;
  },
  getServerError(message: string) {
    let serverError = null;
    if (message) {
      const backendErrorMatch = message.match(
        /\"error_code\"\:\"[0-9][0-9][0-9]\"/
      );
      if (backendErrorMatch?.length) {
        serverError = backendErrorMatch[0]
          .replace('"error_code":"', '')
          .replace('"', '');
      }
    }
    return serverError;
  },
  getGeoBlockError(
    request: XMLHttpRequest | null,
    useRequest = true,
    playerErrorCode: string | null = null,
    playerErrorStatus: number | null = null
  ) {
    if (request?.getResponseHeader || !useRequest) {
      const getMessage = (reason: string) => {
        const message = GeoBlockErrorsMessage[reason];
        if (message) {
          const translatedMessage = message[TranslationManager.currentLanguage];
          return translatedMessage;
        }
        return null;
      };

      // Get reason header if available
      try {
        if (useRequest) {
          playerErrorCode = request?.getResponseHeader('X-TVUP-REASON') || null;
        }
      } catch (e) {
        console.error(e);
      }
      // If header is available, print error message
      if (playerErrorCode || playerErrorStatus === 403) {
        const geoBlockErrors = [GeoBlockErrors.EUROCOPA];
        if (
          playerErrorCode &&
          geoBlockErrors.includes(playerErrorCode as GeoBlockErrors)
        ) {
          return getMessage(playerErrorCode);
        } else {
          return getMessage('default');
        }
      }
    }
    return null;
  },
  getErrorType(
    error?: PlayerError,
    httpCode?: HTTP_ERRORS,
    serverError?: PLAYER_LICENSE_ERRORS | null
  ) {
    let type = error;
    if (httpCode === HTTP_ERRORS.FORBBIDEN) {
      if (serverError === '001') {
        type = PlayerError.MAX_DEVICES;
      }
      if (serverError === '103') {
        type = PlayerError.GEOBLOCK;
      }
    }
    return type;
  },
};

export default PlayerUtils;
